import { supabase } from '../lib/supabase';

export const userAnalyticsService = {
  // ============================================================================
  // DASHBOARD STATISTICS
  // ============================================================================

  async getDashboardStatistics(dateFrom, dateTo) {
    try {
      const [sessions, pageViews, featureUsage, bottlenecks] = await Promise.all([
        this.getUserSessions({ dateFrom, dateTo }),
        this.getPageViews({ dateFrom, dateTo }),
        this.getFeatureUsage({ dateFrom, dateTo }),
        this.getWorkflowBottlenecks({ resolved: false })
      ]);

      // Calculate daily active users
      const uniqueUsers = new Set(sessions?.map(s => s?.userEmail));
      const dailyActiveUsers = uniqueUsers?.size;

      // Calculate feature adoption rate
      const totalFeatures = 10; // Total features in the system
      const usedFeatures = new Set(featureUsage?.map(f => f?.featureName));
      const featureAdoptionRate = ((usedFeatures?.size / totalFeatures) * 100)?.toFixed(1);

      // Calculate average session duration
      const completedSessions = sessions?.filter(s => s?.durationMinutes);
      const avgSessionDuration = completedSessions?.length > 0
        ? (completedSessions?.reduce((sum, s) => sum + s?.durationMinutes, 0) / completedSessions?.length)?.toFixed(1)
        : 0;

      // Calculate productivity score (based on successful actions)
      const successfulActions = featureUsage?.filter(f => f?.success)?.length;
      const totalActions = featureUsage?.length;
      const productivityScore = totalActions > 0
        ? ((successfulActions / totalActions) * 100)?.toFixed(1)
        : 0;

      return {
        dailyActiveUsers,
        featureAdoptionRate: parseFloat(featureAdoptionRate),
        avgSessionDuration: parseFloat(avgSessionDuration),
        productivityScore: parseFloat(productivityScore),
        totalSessions: sessions?.length,
        totalPageViews: pageViews?.length,
        totalFeatureUsage: featureUsage?.length,
        criticalBottlenecks: bottlenecks?.filter(b => b?.severity === 'critical' || b?.severity === 'high')?.length
      };
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error);
      throw error;
    }
  },

  // ============================================================================
  // USER SESSIONS
  // ============================================================================

  async getUserSessions(filters = {}) {
    try {
      let query = supabase?.from('user_sessions')?.select('*')?.order('session_start', { ascending: false });

      if (filters?.dateFrom) {
        query = query?.gte('session_start', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('session_start', filters?.dateTo);
      }
      if (filters?.userEmail) {
        query = query?.eq('user_email', filters?.userEmail);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        userEmail: row?.user_email,
        sessionStart: row?.session_start,
        sessionEnd: row?.session_end,
        durationMinutes: row?.duration_minutes,
        sessionStatus: row?.session_status,
        ipAddress: row?.ip_address,
        userAgent: row?.user_agent,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching user sessions:', error);
      throw error;
    }
  },

  // ============================================================================
  // PAGE VIEWS
  // ============================================================================

  async getPageViews(filters = {}) {
    try {
      let query = supabase?.from('page_views')?.select('*')?.order('viewed_at', { ascending: false });

      if (filters?.dateFrom) {
        query = query?.gte('viewed_at', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('viewed_at', filters?.dateTo);
      }
      if (filters?.userEmail) {
        query = query?.eq('user_email', filters?.userEmail);
      }
      if (filters?.pagePath) {
        query = query?.eq('page_path', filters?.pagePath);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        sessionId: row?.session_id,
        userEmail: row?.user_email,
        pagePath: row?.page_path,
        pageTitle: row?.page_title,
        timeSpentSeconds: row?.time_spent_seconds,
        viewedAt: row?.viewed_at,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.warn('Error fetching page views:', error?.message || error);
      return [];
    }
  },

  async getPageEngagementMetrics() {
    try {
      const { data, error } = await supabase?.from('page_views')?.select('page_path, page_title, time_spent_seconds');

      if (error) throw error;

      // Aggregate by page
      const pageMetrics = {};
      data?.forEach(view => {
        if (!pageMetrics?.[view?.page_path]) {
          pageMetrics[view?.page_path] = {
            pagePath: view?.page_path,
            pageTitle: view?.page_title,
            totalViews: 0,
            totalTimeSpent: 0,
            avgTimeSpent: 0
          };
        }
        pageMetrics[view?.page_path].totalViews += 1;
        pageMetrics[view?.page_path].totalTimeSpent += view?.time_spent_seconds || 0;
      });

      // Calculate averages
      Object.values(pageMetrics)?.forEach(metric => {
        metric.avgTimeSpent = metric?.totalViews > 0
          ? Math.round(metric?.totalTimeSpent / metric?.totalViews)
          : 0;
      });

      return Object.values(pageMetrics)?.sort((a, b) => b?.totalViews - a?.totalViews);
    } catch (error) {
      console.error('Error fetching page engagement metrics:', error);
      throw error;
    }
  },

  // ============================================================================
  // FEATURE USAGE
  // ============================================================================

  async getFeatureUsage(filters = {}) {
    try {
      let query = supabase?.from('feature_usage')?.select('*')?.order('used_at', { ascending: false });

      if (filters?.dateFrom) {
        query = query?.gte('used_at', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('used_at', filters?.dateTo);
      }
      if (filters?.userEmail) {
        query = query?.eq('user_email', filters?.userEmail);
      }
      if (filters?.featureCategory) {
        query = query?.eq('feature_category', filters?.featureCategory);
      }
      if (filters?.department) {
        query = query?.eq('department', filters?.department);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        sessionId: row?.session_id,
        userEmail: row?.user_email,
        featureCategory: row?.feature_category,
        featureName: row?.feature_name,
        actionType: row?.action_type,
        department: row?.department,
        success: row?.success,
        errorMessage: row?.error_message,
        usedAt: row?.used_at,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching feature usage:', error);
      // Return empty array instead of throwing to prevent cascade failures
      return [];
    }
  },

  async getFeatureAdoptionMatrix() {
    try {
      const { data, error } = await supabase?.from('feature_usage')?.select('feature_category, feature_name, success');

      if (error) throw error;

      // Aggregate by feature
      const featureMetrics = {};
      data?.forEach(usage => {
        const key = `${usage?.feature_category}-${usage?.feature_name}`;
        if (!featureMetrics?.[key]) {
          featureMetrics[key] = {
            featureCategory: usage?.feature_category,
            featureName: usage?.feature_name,
            totalUsage: 0,
            successfulUsage: 0,
            adoptionRate: 0
          };
        }
        featureMetrics[key].totalUsage += 1;
        if (usage?.success) {
          featureMetrics[key].successfulUsage += 1;
        }
      });

      // Calculate adoption rates
      Object.values(featureMetrics)?.forEach(metric => {
        metric.adoptionRate = metric?.totalUsage > 0
          ? ((metric?.successfulUsage / metric?.totalUsage) * 100)?.toFixed(1)
          : 0;
      });

      return Object.values(featureMetrics)?.sort((a, b) => b?.totalUsage - a?.totalUsage);
    } catch (error) {
      console.error('Error fetching feature adoption matrix:', error);
      throw error;
    }
  },

  // ============================================================================
  // WORKFLOW BOTTLENECKS
  // ============================================================================

  async getWorkflowBottlenecks(filters = {}) {
    try {
      let query = supabase?.from('workflow_bottlenecks')?.select('*')?.order('identified_at', { ascending: false });

      if (filters?.resolved === false) {
        query = query?.is('resolved_at', null);
      }
      if (filters?.severity) {
        query = query?.eq('severity', filters?.severity);
      }
      if (filters?.department) {
        query = query?.eq('department', filters?.department);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        workflowName: row?.workflow_name,
        stepName: row?.step_name,
        averageDurationSeconds: row?.average_duration_seconds,
        affectedUsersCount: row?.affected_users_count,
        severity: row?.severity,
        department: row?.department,
        identifiedAt: row?.identified_at,
        resolvedAt: row?.resolved_at,
        resolutionNotes: row?.resolution_notes,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching workflow bottlenecks:', error);
      throw error;
    }
  },

  // ============================================================================
  // TRAINING RECOMMENDATIONS
  // ============================================================================

  async getTrainingRecommendations(filters = {}) {
    try {
      let query = supabase?.from('training_recommendations')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.featureCategory) {
        query = query?.eq('feature_category', filters?.featureCategory);
      }
      if (filters?.department) {
        query = query?.eq('department', filters?.department);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        featureCategory: row?.feature_category,
        featureName: row?.feature_name,
        department: row?.department,
        lowAdoptionPercentage: row?.low_adoption_percentage,
        affectedUsersCount: row?.affected_users_count,
        recommendationText: row?.recommendation_text,
        priority: row?.priority,
        status: row?.status,
        createdAt: row?.created_at,
        acknowledgedAt: row?.acknowledged_at
      })) || [];
    } catch (error) {
      console.error('Error fetching training recommendations:', error);
      throw error;
    }
  },

  async acknowledgeRecommendation(recommendationId) {
    try {
      const { data, error } = await supabase
        ?.from('training_recommendations')
        ?.update({
          status: 'acknowledged',
          acknowledged_at: new Date()?.toISOString()
        })
        ?.eq('id', recommendationId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        acknowledgedAt: data?.acknowledged_at
      };
    } catch (error) {
      console.error('Error acknowledging recommendation:', error);
      throw error;
    }
  },

  // ============================================================================
  // ANALYTICS TRACKING (For recording user actions)
  // ============================================================================

  async trackPageView(sessionId, userEmail, pagePath, pageTitle) {
    try {
      const { data, error } = await supabase
        ?.from('page_views')
        ?.insert({
          session_id: sessionId,
          user_email: userEmail,
          page_path: pagePath,
          page_title: pageTitle,
          time_spent_seconds: 0
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error tracking page view:', error);
      throw error;
    }
  },

  async trackFeatureUsage(sessionId, userEmail, featureCategory, featureName, actionType, success = true, errorMessage = null) {
    try {
      const { data, error } = await supabase
        ?.from('feature_usage')
        ?.insert({
          session_id: sessionId,
          user_email: userEmail,
          feature_category: featureCategory,
          feature_name: featureName,
          action_type: actionType,
          success,
          error_message: errorMessage
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error tracking feature usage:', error);
      throw error;
    }
  }
};

export default userAnalyticsService;
